import { useState, useRef, useEffect } from "react";

interface Props {
  workingDirectory: string;
  uiVariant?: "default" | "desktop";
}

const EDITORS = [
  { id: "finder", label: "Finder", icon: "folder" },
  { id: "vscode", label: "VS Code", icon: "code" },
  { id: "cursor", label: "Cursor", icon: "cursor" },
] as const;

export function OpenProjectButton({
  workingDirectory,
  uiVariant = "default",
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isDesktop = uiVariant === "desktop";

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleOpen = async (editorId: string) => {
    setOpen(false);
    if (editorId === "finder") {
      await window.freshvibe.openInFinder(workingDirectory);
    } else {
      await window.freshvibe.openInEditor(
        workingDirectory,
        editorId as "vscode" | "cursor",
      );
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`group inline-flex items-center rounded-md transition-colors hover:bg-stone-100 hover:text-navy dark:hover:bg-surface-hover dark:hover:text-white ${
          isDesktop ? "h-9 gap-1.5 px-2.5 text-stone-300" : "gap-1 px-2 py-1 text-stone-400"
        }`}
        title="Open project"
      >
        <svg
          className={`transition-colors group-hover:text-navy dark:group-hover:text-white ${
            isDesktop ? "h-4 w-4" : "h-3.5 w-3.5"
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
          />
        </svg>
        <span
          className={`font-body transition-colors group-hover:text-navy dark:group-hover:text-white ${
            isDesktop ? "text-sm font-medium leading-none" : "text-[10px]"
          }`}
        >
          Open
        </span>
        <svg
          className={`transition-transform ${open ? "rotate-180" : ""} ${
            isDesktop ? "h-4 w-4" : "h-2.5 w-2.5"
          }`}
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-36 rounded-lg border border-stone-200 dark:border-navy-light bg-white dark:bg-navy shadow-lg py-1 z-50 animate-scale-in">
          {EDITORS.map((editor) => (
            <button
              key={editor.id}
              onClick={() => handleOpen(editor.id)}
              className="w-full text-left px-3 py-1.5 font-body text-xs text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-navy-light hover:text-navy dark:hover:text-gold transition-colors flex items-center gap-2"
            >
              {editor.icon === "folder" && (
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z"
                  />
                </svg>
              )}
              {editor.icon === "code" && (
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"
                  />
                </svg>
              )}
              {editor.icon === "cursor" && (
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.042 21.672 13.684 16.6m0 0-2.51 2.225.569-9.47 5.227 7.917-3.286-.672ZM12 2.25V4.5m5.834.166-1.591 1.591M20.25 10.5H18M7.757 14.743l-1.59 1.59M6 10.5H3.75m4.007-4.243-1.59-1.59"
                  />
                </svg>
              )}
              {editor.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
